import React, { useState, useEffect, useRef } from 'react';
import Header from '../../partials/Header';
import Sidebar from '../../partials/Sidebar';
import LoadingSpinner from '../../components/LoadingSpinner';
import axios from 'axios';
import { Config } from '../../config';

import DiscoveryTab from '../../components/grc/DiscoveryTab';
import ManagementTab from '../../components/grc/ManagementTab';
import { normalizeRiskLevel, riskLevelLabel } from '../../utils/riskLevel';

function KnowledgeBase() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('library');

  // Notification state
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [showWarningMessage, setShowWarningMessage] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [warningMessage, setWarningMessage] = useState('');

  // Shared frameworks data
  const [frameworks, setFrameworks] = useState([]);
  const [filteredFrameworks, setFilteredFrameworks] = useState([]);

  // Library tab filters
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedJurisdiction, setSelectedJurisdiction] = useState('');
  const [selectedFramework, setSelectedFramework] = useState(null);

  // Discovery tab state
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [discoveryResults, setDiscoveryResults] = useState(null);
  const [selectedSources, setSelectedSources] = useState(['federal_register', 'nist', 'iso']);
  const [searchTerms, setSearchTerms] = useState(['cybersecurity', 'data protection', 'privacy']);
  const [customUrls, setCustomUrls] = useState('');
  const [selectedForImport, setSelectedForImport] = useState([]);
  const discoveryResultsRef = useRef(null);

  // Fetch frameworks
  const fetchFrameworks = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${Config.APIBaseURL}/grc/frameworks`, {
        headers: { access_token: `Bearer ${token}` },
        withCredentials: true,
      });
      if (response.data) {
        const fws = Array.isArray(response.data) ? response.data : response.data.frameworks || [];
        setFrameworks(fws);
        setFilteredFrameworks(fws);
      }
    } catch (error) {
      console.error('Error fetching frameworks:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchFrameworks(); }, []);

  // Filter frameworks when search/filter changes
  useEffect(() => {
    let filtered = [...frameworks];
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (fw) =>
          fw.name?.toLowerCase().includes(lower) ||
          fw.short_name?.toLowerCase().includes(lower) ||
          fw.description?.toLowerCase().includes(lower)
      );
    }
    if (selectedCategory) {
      filtered = filtered.filter((fw) => fw.category === selectedCategory);
    }
    if (selectedJurisdiction) {
      filtered = filtered.filter((fw) => fw.jurisdiction === selectedJurisdiction);
    }
    setFilteredFrameworks(filtered);
  }, [frameworks, searchTerm, selectedCategory, selectedJurisdiction]);

  // Notification helpers
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setShowSuccessMessage(true);
    setTimeout(() => setShowSuccessMessage(false), 4000);
  };
  const showError = (msg) => {
    setErrorMessage(msg);
    setShowErrorMessage(true);
    setTimeout(() => setShowErrorMessage(false), 4000);
  };
  const showWarning = (msg) => {
    setWarningMessage(msg);
    setShowWarningMessage(true);
    setTimeout(() => setShowWarningMessage(false), 4000);
  };

  // Utility helpers
  const getUniqueValues = (field) => {
    const values = new Set(frameworks.map((fw) => fw[field]).filter(Boolean));
    return Array.from(values).sort();
  };

  // Keyed on the ISO code first, with the display name as a fallback.
  //
  // Matching only on the English jurisdiction name meant any row whose
  // jurisdiction was written differently — "Unión Europea (eIDAS)" rather than
  // "European Union" — fell through to a blank flag, which reads as missing
  // data rather than as an unrecognised string. The code is the field that is
  // actually constrained: the column documents it as an ISO code or GLOBAL.
  const CODE_FLAGS = {
    US: '🇺🇸', EU: '🇪🇺', GB: '🇬🇧', UK: '🇬🇧', GLOBAL: '🌐',
    DE: '🇩🇪', FR: '🇫🇷', ES: '🇪🇸', BR: '🇧🇷', AU: '🇦🇺', CA: '🇨🇦',
    JP: '🇯🇵', SG: '🇸🇬', KR: '🇰🇷', CN: '🇨🇳', IN: '🇮🇳', DO: '🇩🇴', MX: '🇲🇽',
  };

  const NAME_FLAGS = {
    'United States': '🇺🇸', 'European Union': '🇪🇺', 'United Kingdom': '🇬🇧',
    'International': '🌐', 'Germany': '🇩🇪', 'France': '🇫🇷', 'Spain': '🇪🇸',
    'Brazil': '🇧🇷', 'Australia': '🇦🇺', 'Canada': '🇨🇦', 'Japan': '🇯🇵',
    'Singapore': '🇸🇬', 'South Korea': '🇰🇷', 'China': '🇨🇳', 'India': '🇮🇳',
  };

  const getCountryFlag = (jurisdiction, jurisdictionCode) => {
    const code = (jurisdictionCode || '').toUpperCase();
    return CODE_FLAGS[code] || NAME_FLAGS[jurisdiction] || '🏳️';
  };

  const getTypeDisplayName = (type) => {
    const names = { regulation: 'Regulation', standard: 'Standard', framework: 'Framework', guideline: 'Guideline', law: 'Law', directive: 'Directive' };
    return names[type] || type || 'Unknown';
  };

  const getCategoryDisplayName = (category) => {
    const names = {
      cybersecurity: 'Cybersecurity', data_protection: 'Data Protection', privacy: 'Privacy',
      financial: 'Financial', healthcare: 'Healthcare', government: 'Government',
      telecommunications: 'Telecommunications', energy: 'Energy', general: 'General',
      critical_infrastructure: 'Critical Infrastructure', risk_management: 'Risk Management',
      cloud_security: 'Cloud Security', iot_security: 'IoT Security',
    };
    return names[category] || category || 'Unknown';
  };

  if (loading) return <LoadingSpinner />;

  const TAB_CLASSES = (tab) =>
    `py-2 px-1 border-b-2 font-medium text-sm ${
      activeTab === tab
        ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
        : 'border-transparent text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300 hover:border-slate-300'
    }`;

  return (
    <div className="flex h-[100dvh] overflow-hidden">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

      <div className="relative flex flex-col flex-1 overflow-y-auto overflow-x-hidden">
        <Header sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />

        <main className="grow">
          <div className="px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto">
            {/* Page header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
                    Compliance Knowledge Base
                  </h1>
                  <p className="text-slate-600 dark:text-slate-400 mt-2">
                    Explore, discover, and manage compliance frameworks and regulations
                  </p>
                </div>
                <div className="bg-white dark:bg-slate-800 rounded-lg p-1 shadow-sm border border-slate-200 dark:border-slate-700">
                  <div className="text-sm text-slate-600 dark:text-slate-400 px-3 py-1">
                    {frameworks.length} frameworks
                  </div>
                </div>
              </div>
            </div>

            {/* Notifications */}
            {showSuccessMessage && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400">
                {successMessage}
              </div>
            )}
            {showErrorMessage && (
              <div className="mb-4 p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-400">
                {errorMessage}
              </div>
            )}
            {showWarningMessage && (
              <div className="mb-4 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-sm text-amber-700 dark:text-amber-400">
                {warningMessage}
              </div>
            )}

            {/* Tab Navigation */}
            <div className="mb-6">
              <div className="border-b border-slate-200 dark:border-slate-700">
                <nav className="-mb-px flex space-x-8">
                  <button onClick={() => setActiveTab('library')} className={TAB_CLASSES('library')}>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                      </svg>
                      Framework Library
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('discovery')} className={TAB_CLASSES('discovery')}>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                      Discovery
                    </div>
                  </button>
                  <button onClick={() => setActiveTab('management')} className={TAB_CLASSES('management')}>
                    <div className="flex items-center">
                      <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Management
                    </div>
                  </button>
                </nav>
              </div>
            </div>

            {/* Tab Content */}
            {activeTab === 'library' && (
              <div className="space-y-6">
                {/* Search and Filters */}
                <div className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Search</label>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Name, acronym, or description..."
                        className="w-full text-sm px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Category</label>
                      <select
                        value={selectedCategory}
                        onChange={(e) => setSelectedCategory(e.target.value)}
                        className="w-full text-sm px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">All Categories</option>
                        {getUniqueValues('category').map((c) => (
                          <option key={c} value={c}>{getCategoryDisplayName(c)}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-2">Jurisdiction</label>
                      <select
                        value={selectedJurisdiction}
                        onChange={(e) => setSelectedJurisdiction(e.target.value)}
                        className="w-full text-sm px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-200 focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">All Jurisdictions</option>
                        {getUniqueValues('jurisdiction').map((j) => (
                          <option key={j} value={j}>{j}</option>
                        ))}
                      </select>
                    </div>
                    <div className="flex items-end">
                      <button
                        onClick={() => { setSearchTerm(''); setSelectedCategory(''); setSelectedJurisdiction(''); }}
                        className="w-full text-sm px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
                      >
                        Clear Filters
                      </button>
                    </div>
                  </div>
                </div>

                {/* Framework Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredFrameworks.map((framework) => (
                    <div
                      key={framework.id}
                      className="bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-200 dark:border-slate-700 p-6 hover:shadow-xl transition-shadow cursor-pointer"
                      onClick={() => setSelectedFramework(framework)}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-bold text-slate-800 dark:text-slate-100 mb-1">{framework.name}</h3>
                          <p className="text-sm text-slate-600 dark:text-slate-400">{framework.short_name}</p>
                        </div>
                        {/* The column stores lowercase ("critical", "high", …)
                            while this compared capitalised, so a row with a risk
                            level set still fell through to the neutral style.
                            And an unset level rendered an empty green pill,
                            which reads as "low" rather than as "not assessed" —
                            the badge is omitted instead. */}
                        {framework.risk_level && (
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          normalizeRiskLevel(framework.risk_level) === 'critical' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200'
                          : normalizeRiskLevel(framework.risk_level) === 'high' ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-800 dark:text-orange-200'
                          : normalizeRiskLevel(framework.risk_level) === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-200'
                          : 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-200'
                        }`}>{riskLevelLabel(framework.risk_level)}</span>
                        )}
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex items-center text-sm text-slate-600 dark:text-slate-400">
                          <span className="mr-2">{getCountryFlag(framework.jurisdiction, framework.jurisdiction_code)}</span>
                          {framework.jurisdiction} · {getTypeDisplayName(framework.type)} · {getCategoryDisplayName(framework.category)}
                        </div>
                      </div>
                      {framework.description && (
                        <p className="text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{framework.description}</p>
                      )}
                    </div>
                  ))}
                </div>

                {filteredFrameworks.length === 0 && (
                  <div className="text-center py-12">
                    <h3 className="text-lg font-medium text-slate-900 dark:text-slate-100 mb-2">No frameworks found</h3>
                    <p className="text-slate-500 dark:text-slate-400">Try adjusting your search terms or filters</p>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'discovery' && (
              <DiscoveryTab
                frameworks={frameworks}
                isDiscovering={isDiscovering}
                setIsDiscovering={setIsDiscovering}
                discoveryResults={discoveryResults}
                setDiscoveryResults={setDiscoveryResults}
                selectedSources={selectedSources}
                setSelectedSources={setSelectedSources}
                searchTerms={searchTerms}
                setSearchTerms={setSearchTerms}
                customUrls={customUrls}
                setCustomUrls={setCustomUrls}
                selectedForImport={selectedForImport}
                setSelectedForImport={setSelectedForImport}
                discoveryResultsRef={discoveryResultsRef}
                onSuccess={showSuccess}
                onError={showError}
                onRefreshFrameworks={fetchFrameworks}
              />
            )}

            {activeTab === 'management' && (
              <ManagementTab
                frameworks={frameworks}
                onSuccess={showSuccess}
                onError={showError}
                onWarning={showWarning}
                onRefreshFrameworks={fetchFrameworks}
              />
            )}
          </div>
        </main>
      </div>
    </div>
  );
}

export default KnowledgeBase;
